import { FunctionComponent, RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import silence from  "./5-seconds-of-silence.mp3";
import rain from "./rain-03.mp3";
import { Player, PlayerReference } from 'video-react';
import 'video-react/dist/video-react.css';

interface IProps {
	id: string;
	width: string;
	height: string;
	source: string;
	album: string;
	videoTitle: string | null;
	artSrc: string;
	video: RefObject<HTMLVideoElement>;
	controls: boolean;
	nextVideo: () => void;
	prevVideo: () => void;
	onLoaded: () => void;
	onPlaying: () => void;
	onPause: () => void;
	onEnded: () => void;
	onVolumeChange: (event: React.SyntheticEvent<Element>) => void;
	onTouchStart: (event: React.TouchEvent<Element>) => void;
	onTouchMove: (event: React.TouchEvent<Element>) => void;
	onTouchEnd: () => void;
	onStalled: () => void;
	onLoadStart: () => void;
	onProgress: () => void;
}

const VideoWithMediaSession: FunctionComponent<IProps> = (props) => {
	const { id, width, height, source, album, controls, videoTitle, artSrc, video, nextVideo, prevVideo, onLoaded, onPlaying, onPause, onEnded, onVolumeChange, onTouchEnd, onTouchMove, onTouchStart, onStalled, onLoadStart, onProgress } = props;
	const audio = useRef<HTMLVideoElement>(null);

	const updatePositionState = useCallback(() => {
		onProgress();

		// @ts-ignore
		if (!(video.current && 'mediaSession' in navigator && 'setPositionState' in navigator.mediaSession)) { return; }
		const duration = video.current.duration;
		if (isNaN(duration) || !isFinite(duration)) {
			return;
		}

		// @ts-ignore
		navigator.mediaSession.setPositionState({
			duration: video.current.duration,
			playbackRate: video.current.playbackRate,
			position: video.current.currentTime,
		});
	}, [onProgress, video]);

	const onVideoLoaded = useCallback(() => {
		if (!video.current) { return; }
		onLoaded();
		if ('mediaSession' in navigator) {
			(navigator as any).mediaSession.playbackState = video.current.paused ? 'paused' : "playing";
		}
		updatePositionState();
		if (audio.current) {
			audio.current.play();
		}
	}, [onLoaded, updatePositionState, video]);

	const onVideoPlaying = useCallback((e: any) => {
		e.target.blur();
		if ('mediaSession' in navigator) {
			(navigator as any).mediaSession.playbackState = 'playing';
		}
		if (audio.current) {
			audio.current.pause();
		}
		updatePositionState();
		onPlaying();
	}, [onPlaying, updatePositionState]);

	const onVideoPaused = useCallback((e: any) => {
		e.target.blur();
		if ('mediaSession' in navigator) {
			(navigator as any).mediaSession.playbackState = 'paused';
		}
		updatePositionState();
		onPause();
	}, [onPause, updatePositionState]);

	const onVideoEnded = useCallback(() => {
		if ('mediaSession' in navigator) {
			(navigator as any).mediaSession.playbackState = 'paused';
		}
		if (audio.current) {
			audio.current.play();
		}
		updatePositionState();
		onEnded();
	}, [onEnded, updatePositionState]);

	const togglePlay = useCallback(async () => {
		if (!video.current) { return; }
		if (video.current.paused) {
			await video.current.play();
			return;
		}
		video.current.pause();
	}, [video]);

	const setMediaSession = useCallback(() => {
		if (!video.current || !('mediaSession' in navigator) || !source) { return; }
		// console.log("start media");
		// console.log(artSrc);
		// console.log(videoTitle);
		// @ts-ignore
		const { mediaSession } = navigator;
		mediaSession.setActionHandler('play', togglePlay);
		mediaSession.setActionHandler('pause', togglePlay);
		mediaSession.setActionHandler('nexttrack', nextVideo);
		mediaSession.setActionHandler('previoustrack', prevVideo);
		// @ts-ignore
		mediaSession.metadata = new MediaMetadata({
			title: videoTitle ?? "",
			album: album,
			artwork: [{
				src: artSrc,
			}],
		});
	}, [album, artSrc, nextVideo, togglePlay, prevVideo, source, video, videoTitle]);

	useEffect(() => {
		if (!video.current || !('mediaSession' in navigator) || !source) { return; }
		
		// @ts-ignore
		const { mediaSession } = navigator;
		setMediaSession();

		return () => {
			// console.log("stop media");
			mediaSession.setActionHandler('play', null);
			mediaSession.setActionHandler('pause', null);
			mediaSession.setActionHandler('nexttrack', null);
			mediaSession.setActionHandler('previoustrack', null);
			mediaSession.metadata = null;
		};
	}, [album, artSrc, nextVideo, prevVideo, setMediaSession, source, updatePositionState, video, videoTitle]);

	const onAudioPlaying = useCallback(() => {
		if (!audio.current) { return; }
		audio.current.volume = 0.00;
		setMediaSession();
	}, [setMediaSession]);

	useEffect(() => {
		const onClick = (e: any) => {
			e.target.blur();
		}

		document.addEventListener("fullscreenchange", onClick);
		return () => {
			document.removeEventListener("fullscreenchange", onClick);
		}
	})

	const raidSound = useMemo(() => {
		if (navigator.userAgent.indexOf("AppleWebKit") >= 0) {
			return null;
		}
		return <video src={rain} style={{ display: "none" }} loop ref={audio} onPlaying={onAudioPlaying} />;
	}, [onAudioPlaying]);

	// slop
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		const video2 = video.current;
		if (!video2) return;

		const HIDE_DELAY = 1000;

		const hideControls = () => {
			if (video2.paused) return;
			video2.controls = false;
			video2.style.cursor = "none";
		};

		const showControls = () => {
			video2.controls = true;
			video2.style.cursor = "unset";

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			if (video2.paused) { return; }

			timeoutRef.current = setTimeout(() => {
				hideControls();
			}, HIDE_DELAY);
		};

		const onPause = () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			video2.controls = true;
			video2.style.cursor = "unset";
		}

		const onPlay = () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			timeoutRef.current = setTimeout(() => {
				hideControls();
			}, HIDE_DELAY);
		}

		const onClick = () => {
			console.log("click");
			if (video2.controls === true) {
				return;
			}
			if (!video2.paused) {
				video2.pause();
			}
		}
		const clickEvents = ["touchstart", "mousedown"];
		clickEvents.forEach((event) => {
			video2.addEventListener(event, onClick);
		})

		// Events that should show controls
		const events = ["mousemove", "touchstart", "pause", "mousedown", "mouseup"];

		events.forEach((event) => {
			video2.addEventListener(event, showControls);
		});
		video2.addEventListener("pause", onPause);
		video2.addEventListener("play", onPlay);

		

		// Initial trigger (important for auto-advance case)
		showControls();

		return () => {
			events.forEach((event) => {
				video2.removeEventListener(event, showControls);
			});
			video2.removeEventListener("pause", onPause);
			video2.removeEventListener("play", onPlay);
			clickEvents.forEach((event) => {
				video2.removeEventListener(event, onClick);
			})

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);
	// end slop

	return <>
		<video id={id} className="videoWithMedia" width={width} height={height} controls={controls} ref={video}
			onLoadedData={onVideoLoaded}
			onPlay={onVideoPlaying}
			onPause={onVideoPaused}
			onEnded={onVideoEnded}
			onVolumeChange={onVolumeChange}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
			// onStalled={onStalled}
			onError={onStalled}
			onLoadStart={onLoadStart}
			onProgress={updatePositionState}
			onClick={(e: any) => { e.target.blur?.(); }}>
			<source src={source} type="video/mp4"></source>
			Your browser does not support video.
		</video>
		{raidSound}
	</>;

	// return <div id={id}>
	// 	<Player width={Number(width)} fluid={false} ref={video}>
	// 		<source src={source} />
	// 	</Player>
	// </div>
}

export default VideoWithMediaSession;